import React, { useCallback, useEffect, useState } from 'react';
import { useSettingsQuery } from './queries/Settings';

const RESET_EVENT = 'songrequests:reset';
const REQUESTED_EVENT = 'songrequests:requested';

export function TrackCard({ track }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [requesterName, setRequesterName] = useState('');
  const [message, setMessage] = useState('');
  const [isRequested, setIsRequested] = useState(false);
  const [requestTime, setRequestTime] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { data: settings } = useSettingsQuery();
  const commentsEnabled = settings?.enableRequestComments ?? false;

  const parts = (track.artistTitle || '').includes(' - ')
    ? track.artistTitle.split(' - ', 2)
    : ['', track.artistTitle || ''];
  const artist = parts[0];
  const song = parts[1];

  useEffect(() => {
    if (!isRequested) return;
    const timer = setTimeout(() => setIsRequested(false), 6_000);
    return () => clearTimeout(timer);
  }, [isRequested]);

  const openConfirm = () => {
    setRequesterName('');
    setMessage('');
    setErrorMessage(null);
    setShowConfirm(true);
  };

  const closeConfirm = useCallback(() => {
    setRequesterName('');
    setMessage('');
    setErrorMessage(null);
    setShowConfirm(false);
  }, []);

  useEffect(() => {
    const onReset = () => closeConfirm();
    window.addEventListener(RESET_EVENT, onReset);
    return () => window.removeEventListener(RESET_EVENT, onReset);
  }, [closeConfirm]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    fetch('/api/requestTrack', {
      method: 'POST',
      body: JSON.stringify({
        trackGuid: track.guid,
        requestedBy: (requesterName || '').trim() || 'Anonymous',
        ...(commentsEnabled ? { message: (message || '').trim() || undefined } : {}),
      }),
      headers: { 'Content-Type': 'application/json' },
    })
      .then(async (response) => {
        if (response.ok) {
          setIsRequested(true);
          setRequestTime(new Date());
          closeConfirm();
          window.dispatchEvent(new Event(REQUESTED_EVENT));
          return;
        }

        let data: any = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }

        if (response.status === 409) {
          setErrorMessage((data && data.message) || 'This song has already been requested recently.');
        } else if (response.status === 400) {
          setErrorMessage((data && data.message) || 'Invalid request parameters.');
        } else {
          setErrorMessage((data && data.message) || 'An error occurred while requesting the song');
        }
      })
      .catch(() => setErrorMessage('Network error. Please try again.'));
  };

  const getTimeSinceRequest = () => {
    if (!requestTime) return '';
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - requestTime.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Playing soon...';
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  };

  return (
    <>
      <div
        onClick={openConfirm}
        className={`bg-white rounded-lg shadow-md p-3 lg:p-4 hover:shadow-lg transition-shadow duration-200 cursor-pointer 
          ${isRequested ? 'border-2 border-green-500' : ''}`}
      >
        <div className="text-base lg:text-lg font-semibold text-[#292d6e]">{track.artistTitle}</div>

        {isRequested && (
          <div className="mt-2 text-sm text-green-600 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Requested! {getTimeSinceRequest()}
          </div>
        )}
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeConfirm}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {errorMessage ? 'Song Request' : 'Confirm Song Request'}
            </h3>

            {!errorMessage ? (
              <div className="mb-6 space-y-2">
                <div className="text-lg lg:text-xl text-gray-900">
                  <span className="font-semibold text-gray-800">Song:</span>{' '}
                  <span className="font-normal text-gray-700">{song}</span>
                </div>

                <div className="text-lg lg:text-xl text-gray-900">
                  <span className="font-semibold text-gray-800">Artist:</span>{' '}
                  <span className="font-normal text-gray-700">{artist}</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 mb-4">Unable to request: {track.artistTitle}</p>
            )}

            {errorMessage && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">{errorMessage}</div>
            )}

            {!errorMessage ? (
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="requesterName" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="requesterName"
                    name="requesterName"
                    autoFocus
                    value={requesterName}
                    onChange={(e) => setRequesterName(e.target.value)}
                    placeholder="Enter your name (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {commentsEnabled && (
                  <div className="mb-4">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Comment
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={3}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      maxLength={settings?.maxMessageLength}
                      placeholder="Add a note (optional)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="mt-1 text-xs text-gray-500 text-right">
                      {message.length}/{settings?.maxMessageLength || 150}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeConfirm}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors cursor-pointer"
                  >
                    Request Song
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex justify-center">
                <button
                  autoFocus
                  type="button"
                  onClick={closeConfirm}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
